/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { Button, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { INileNewsletterViewProps } from './nile-newsletter';
import { INileNewsletterProps } from '../nile-newsletter/nile-newsletter.props.autogenerated';
import classnames from 'classnames';
import { IGetMailchimpSubscriptionStatusData } from '../../actions/get-mailchimp-subscription-status.action';
import { changeMailChimpSubscriptionStatusAsync, setUserBirthdayAsync } from '../../actions/DataActionExtension.g';

import { MouseEventHandler } from 'react';
import { AsyncResult } from '@msdyn365-commerce/retail-proxy';
import { IGetBirthdayResponse } from '../../actions/get-user-birthday.action';
import { padStart } from 'lodash';

interface IMailchimpSignupPreferences {
    yesText: string;
    noText: string;
    newsletterYesText: string;
    physicalYesText: string;
    mailchimpState: IMailchimpState;
    handler: MouseEventHandler<HTMLButtonElement>;
    physicalHandler: MouseEventHandler<HTMLButtonElement>;
}
interface IMailChimpSubmitPreferences {
    submitButtonText: string;
    handler: MouseEventHandler<HTMLButtonElement>;
}
interface IMailchimpState {
    isModalOpen: boolean;
    newsletterSubscribed: boolean;
    physicalSubscribed: boolean;
    dayString: string | undefined;
    bDay: number;
    bMonth: number;
    bYear: number;
    bdayError: string;
    newsletterCookieSet: boolean;
    showBirthday: boolean;
    showNewsletter: boolean;
}

interface IBdayPreferences {
    state: IMailchimpState;
    onEditBirthdayInput: any;
}

export interface INileNewsletterData {
    mailchimpSubscriptionStatus: AsyncResult<IGetMailchimpSubscriptionStatusData>;
    birthday: AsyncResult<IGetBirthdayResponse>;
}

const MailchimpSignupSection: React.FC<IMailchimpSignupPreferences & INileNewsletterProps<INileNewsletterData>> = ({
    physicalYesText,
    newsletterYesText,
    yesText,
    noText,
    mailchimpState,
    handler,
    physicalHandler
}) => {
    const newsletterClassText = mailchimpState.newsletterSubscribed ? 'enable' : 'disable';
    const physicalClassText = mailchimpState.physicalSubscribed ? 'enable' : 'disable';
    const classNames = classnames('ms-nile-newsletter__container', ``);
    const buttonWrapper = {
        className: classnames('ms-nile-newsletter__container__flex', ``)
    };

    const button = (
        <Button
            className={classnames('ms-account-profile__toggle-button', `ms-account-profile__toggle-${newsletterClassText}-button`)}
            aria-label={'Mailchimp Subscription'}
            aria-pressed={!mailchimpState.newsletterSubscribed}
            onClick={handler}
        />
    );
    const physicalButton = (
        <Button
            className={classnames('ms-account-profile__toggle-button', `ms-account-profile__toggle-${physicalClassText}-button`)}
            aria-label={'Mailchimp Mail Subscription'}
            aria-pressed={!mailchimpState.physicalSubscribed}
            onClick={physicalHandler}
        />
    );
    //Yes, I would like to recieve newsletter.

    const buttonYesText = <span className='ms-nile-newsletter__yes-text'>{yesText} </span>;
    const buttonNoText = <span>{noText}</span>;

    const buttonPhysicalYesText = <span className='ms-nile-newsletter__yes-text'>{yesText}</span>;
    const buttonPhysicalNoText = <span>{noText}</span>;
    return (
        <>
            <Node className={classNames}>
                <Node {...buttonWrapper}>
                    <div className='ms-nile-newsletter__button'>
                        {buttonNoText}
                        {button}
                        {buttonYesText}
                    </div>
                    <p className='ms-nile-newsletter__button-text'>{newsletterYesText}</p>
                </Node>
            </Node>
            <Node className={classNames}>
                <Node {...buttonWrapper}>
                    <div className='ms-nile-newsletter__button'>
                        {buttonPhysicalNoText}
                        {physicalButton}
                        {buttonPhysicalYesText}
                    </div>
                    <p className='ms-nile-newsletter__button-text'>{physicalYesText}</p>
                </Node>
            </Node>
        </>
    );
};
const MailchimpSubmitButton: React.FC<IMailChimpSubmitPreferences & INileNewsletterProps<INileNewsletterData>> = ({
    submitButtonText,
    handler
}) => {
    const submitButton = (
        <Button className='ms-nile-newsletter__current-site' onClick={handler}>
            {submitButtonText}
        </Button>
    );
    // const classNames = classnames('ms-account-profile__preference', `ms-account-profile__preference-web-tracking`, {
    //     'ms-account-profile__personalization-updating': true
    // });
    return (
        <>
            <div>{submitButton}</div>
        </>
    );
};

const NewsletterBirthdaySection: React.FC<IBdayPreferences & INileNewsletterProps<INileNewsletterData>> = ({
    state,
    onEditBirthdayInput
}) => {
    return (
        <>
            <input
                className={'ms-nile-newsletter__birthday-input'}
                type='date'
                min={'1900-01-01'}
                max={new Date().toDateString()}
                id='bday'
                name='bday-entry'
                value={state.dayString}
                onChange={onEditBirthdayInput}
            />
            <p className='ms-account-profile__error'>{state.bdayError}</p>
        </>
    );
};

export interface ICookieSetOptions {
    path?: string;
    expires?: Date;
    maxAge?: number;
    domain?: string;
    secure?: boolean;
    httpOnly?: boolean;
    sameSite?: boolean | 'none' | 'lax' | 'strict';
}
export class NileNewsletterView extends React.PureComponent<
    INileNewsletterViewProps & INileNewsletterProps<INileNewsletterData>,
    IMailchimpState
> {
    private readonly _preferredNewsLettertorageKey: string = '_msdyn365___newsletter_popup';
    public string = '_msdyn365__birthday_';
    constructor(props: INileNewsletterViewProps & INileNewsletterProps<INileNewsletterData>) {
        super(props);
        this.getIsModalOpenNeeded();
    }
    public getIsModalOpenNeeded = () => {
        const {
            data: { mailchimpSubscriptionStatus, birthday }
        } = this.props;
        const status = mailchimpSubscriptionStatus.result;
        const birthdayStatus = birthday.result;
        //When to display:
        //You are not subscribed to either physical or newsletter and this popup has not shown for X time
        //You have not entered your birthday and this popup has not shown for X time.

        const newsletterSubscribedValue: boolean = status?.newsletterActive || false;
        const physicalSubscribedValue: boolean = status?.physicalActive || false;

        const bDayint: number = birthdayStatus?.Day || -1;
        const bMonthint: number = birthdayStatus?.Month || -1;
        const bYearint: number = birthdayStatus?.Year || -1;

        let isCookieValueSet: boolean = false;

        let isModalOpenState = !(newsletterSubscribedValue && physicalSubscribedValue) || bDayint === -1;

        if (this.props.context.request.cookies.get(this._preferredNewsLettertorageKey).value) {
            isCookieValueSet = true;
        }
        if (isCookieValueSet) {
            isModalOpenState = false;
        }

        this.state = {
            newsletterSubscribed: newsletterSubscribedValue,
            physicalSubscribed: physicalSubscribedValue,
            isModalOpen: isModalOpenState,
            dayString: undefined,
            bDay: bDayint,
            bMonth: bMonthint,
            bYear: bYearint,
            bdayError: '',
            newsletterCookieSet: isCookieValueSet,
            showBirthday: bDayint === -1 || bMonthint === -1 || bYearint === -1,
            showNewsletter: !(newsletterSubscribedValue && physicalSubscribedValue)
        };
    };
    private readonly _writeToCookie = (storageKey?: string, storageValue?: string): void => {
        if (!storageKey || !storageValue) {
            return;
        }
        const cookieOptions: ICookieSetOptions = { secure: true, sameSite: 'none' };
        cookieOptions.maxAge = this.props.config.cookieLifetime;
        this.props.context.request.cookies.set(storageKey, storageValue, cookieOptions);
    };
    public handleMailChimpSubscriptionStatusClick = () => {
        changeMailChimpSubscriptionStatusAsync(
            { callerContext: this.props.context.actionContext },
            !this.state.newsletterSubscribed,
            false
        ).then(res => {
            if (res.Success) {
                this.setState({ newsletterSubscribed: !this.state.newsletterSubscribed });
                this._writeToCookie(this._preferredNewsLettertorageKey, this.state.newsletterSubscribed.toString());
            }
        });
    };
    public handleMailChimpPhysicalSubscriptionStatusClick = () => {
        changeMailChimpSubscriptionStatusAsync(
            { callerContext: this.props.context.actionContext },
            !this.state.physicalSubscribed,
            true
        ).then(res => {
            if (res.Success) {
                this.setState({ physicalSubscribed: !this.state.physicalSubscribed });
                this._writeToCookie(this._preferredNewsLettertorageKey, this.state.newsletterSubscribed.toString());
            }
        });
    };
    public handlSubmiteMailChimpSubscriptionStatusClick = async () => {
        //    this.handleMailChimpPhysicalSubscriptionStatusClick();
        //    this.handleMailChimpSubscriptionStatusClick();
        if (this.state.bDay === -1) {
            const success = await this.submitBirthday();
            if (success) {
                if (this.state.newsletterSubscribed === true || this.state.physicalSubscribed === true) {
                    this._writeToCookie(this._preferredNewsLettertorageKey, this.state.newsletterSubscribed.toString());
                }
                this._writeToCookie(this._preferredNewsLettertorageKey, 'true');
                this.setState({
                    isModalOpen: false
                });
            }
        } else {
            if (this.state.newsletterSubscribed === true || this.state.physicalSubscribed === true) {
                this._writeToCookie(this._preferredNewsLettertorageKey, this.state.newsletterSubscribed.toString());
            }
            this.setState({
                isModalOpen: false
            });
        }
    };

    public CloseModal = (): void => {
        this._writeToCookie(this._preferredNewsLettertorageKey, this.state.newsletterSubscribed.toString());
        this.setState({
            isModalOpen: false
        });
    };

    public onEditBirthdayInput = (e: any) => {
        this.setState({ dayString: e.target.value });
        return false;
    };

    public submitBirthday = async (): Promise<boolean | undefined> => {
        const customerAccountNum = this.props.context.request.user.customerAccountNumber;
        if (this.state.dayString && customerAccountNum) {
            if (
                new Date(this.state.dayString) &&
                new Date(this.state.dayString).getFullYear() < new Date().getFullYear() &&
                new Date(this.state.dayString).getFullYear() > 1901 //Magic "Not before 1901 year plz"
            ) {
                //Is this a valid date, and is it before today?
                const splitDate: string[] = this.state.dayString.split('-');

                const result = await setUserBirthdayAsync(
                    { callerContext: this.props.context.actionContext },
                    parseInt(splitDate[2], 10),
                    parseInt(splitDate[1], 10),
                    parseInt(splitDate[0], 10),
                    customerAccountNum
                );

                this.setState({
                    bDay: parseInt(splitDate[2], 10),
                    bMonth: parseInt(splitDate[1], 10),
                    bYear: parseInt(splitDate[0], 10),
                    bdayError: ''
                });

                return result !== undefined;
            } else {
                this.setState({
                    bdayError: this.props.resources.invalidDateError,
                    dayString: `${this.state.bYear}-${padStart(this.state.bMonth.toString(), 2, '0')}-${padStart(
                        this.state.bDay.toString(),
                        2,
                        '0'
                    )}`
                });
                return false;
            }
        }
        this.setState({
            bdayError: this.props.resources.invalidDateError
        });
        return false;
    };

    public render(): JSX.Element | null {
        const {
            resources: {
                heading,
                mailchimpModalYesText,
                mailchimpModalNoText,
                submitButtonText,
                mailchimpPhysicalYesText,
                mailchimpYesText
            },
            modalContainer,
            modalBodyContainer,
            modalFooterContainer
        } = this.props;
        return this.state.isModalOpen && this.props.context.actionContext.requestContext.user.isAuthenticated ? (
            <Module {...modalContainer}>
                <Node className='ms-nile-newsletter-main-dev'>
                    <div className='ms-nile-newsletter__modal-header msc-modal__header'>
                        <button type='button' className='msc-modal__close-button' aria-label='Close' onClick={this.CloseModal}></button>
                    </div>
                </Node>
                {this.state.showNewsletter ? (
                    <Node {...modalBodyContainer}>
                        <b>{heading}</b>
                        <hr />

                        <MailchimpSignupSection
                            {...this.props}
                            physicalYesText={mailchimpPhysicalYesText}
                            newsletterYesText={mailchimpModalYesText}
                            yesText={mailchimpYesText}
                            noText={mailchimpModalNoText}
                            mailchimpState={this.state}
                            handler={this.handleMailChimpSubscriptionStatusClick}
                            physicalHandler={this.handleMailChimpPhysicalSubscriptionStatusClick}
                        />
                    </Node>
                ) : (
                    <></>
                )}

                {this.state.showBirthday ? (
                    <Node {...modalFooterContainer}>
                        <h4 className={'ms-nile-newsletter__section-heading'}>{this.props.resources.birthdayHeading} </h4>
                        <hr />
                        <NewsletterBirthdaySection
                            {...this.props}
                            state={this.state}
                            onEditBirthdayInput={this.onEditBirthdayInput}
                        ></NewsletterBirthdaySection>
                    </Node>
                ) : (
                    <></>
                )}

                <Node {...modalFooterContainer}>
                    <MailchimpSubmitButton
                        {...this.props}
                        submitButtonText={submitButtonText}
                        handler={this.handlSubmiteMailChimpSubscriptionStatusClick}
                    />
                </Node>
            </Module>
        ) : null;
    }
}
export default NileNewsletterView;
